import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CategoryCard from "../../../../components/card/CategoryCard";
import useHorizontalWheel from "../../../../hooks/useHorizontalWheel";
import { scrollProductStripChunk } from "../../../../utils/productStripScroll";
import "./ShopByCategory.css";

const getCategoryParentId = (c) =>
  c?.parent_id ?? c?.parentId ?? c?.parent?._id ?? c?.parent;

const selectMainCategories = (list) => {
  if (!list?.length) return [];
  const mains = list.filter((c) => {
    const p = getCategoryParentId(c);
    return (
      p == null ||
      p === "" ||
      String(p) === "0" ||
      (Array.isArray(p) && p.length === 0)
    );
  });
  return mains.length > 0 ? mains : list;
};

const ShopByCategory = ({ categories }) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const displayCategories = useMemo(
    () => selectMainCategories(categories),
    [categories]
  );

  const hasArrows = displayCategories.length > 5;
  const isCentered = displayCategories.length <= 5;

  useHorizontalWheel(scrollerRef, {
    axisRatio: 0.88,
    mapWheelYToHorizontal: true,
    wheelYVsXRatio: 0.3,
  });

  const updateArrows = useCallback(() => {
    if (!hasArrows) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, [hasArrows]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !hasArrows) return;
    updateArrows();
    scroller.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);
    return () => {
      scroller.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, displayCategories, hasArrows]);

  const scrollBy = (direction) => {
    scrollProductStripChunk(scrollerRef.current, direction);
  };

  if (!displayCategories.length) {
    return null;
  }

  return (
    <section className="home-shop-category">
      <div className="home-shop-category__header">
        <h2 className="home-shop-category__title">SHOP BY CATEGORY</h2>
      </div>

      <div className={`home-shop-category__container ${hasArrows ? "home-shop-category__container--has-arrows" : ""}`}>
        {hasArrows && canScrollLeft && (
          <button
            type="button"
            className="home-shop-category__nav home-shop-category__nav--prev"
            onClick={() => scrollBy(-1)}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>
        )}

        <div className="home-shop-category__scroller" ref={scrollerRef}>
          <div className={`home-shop-category__track ${isCentered ? "home-shop-category__track--centered" : ""}`}>
            {displayCategories.map((category) => (
              <div key={category?._id} className="home-shop-category__item">
                <CategoryCard category={category} />
              </div>
            ))}
          </div>
        </div>

        {hasArrows && canScrollRight && (
          <button
            type="button"
            className="home-shop-category__nav home-shop-category__nav--next"
            onClick={() => scrollBy(1)}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
        )}
      </div>
    </section>
  );
};

export default ShopByCategory;
